import type {
  I18n,
  Locale,
  OutlineCollectionRef,
  OutlineCopyRef,
  OutlineRegion,
  PageOutline,
} from '@/lib/api/types';
import {
  getAtPath,
  isImageNode,
  isPlainObject,
  type Json,
  type Path,
} from '@/features/settings/pageCopyTree';

/**
 * Pure helpers over the page outline (GET /admin/page-outlines) and the namespace documents
 * the page editor holds. Page copy is stored locale-first (`{ en: {…}, ar: {…} }`), and a
 * region addresses a subtree by a dot path that exists under both trees.
 */

/** namespace → whole working document (`{ en, ar }`). */
export type Docs = Record<string, Json>;

/** The tree path of a copy reference in one locale. */
export function refPath(ref: OutlineCopyRef, locale: Locale): Path {
  return [locale, ...ref.path.split('.')];
}

/** The subtree a copy reference addresses, in one locale. */
export function refValue(docs: Docs, ref: OutlineCopyRef, locale: Locale): Json | undefined {
  const doc = docs[ref.namespace];
  return doc === undefined ? undefined : getAtPath(doc, refPath(ref, locale));
}

export function resolveLabel(label: I18n, locale: Locale): string {
  return (locale === 'ar' ? label.ar : label.en) || label.en;
}

/**
 * Keys whose value is layout or wiring, not prose: the same in both languages. The editor
 * shows them once and writes both trees; the rarely-touched ones sit under "Layout settings".
 */
export const SHARED_VALUE_KEYS = new Set([
  'href',
  'accent',
  'icon',
  'ratio',
  'key',
  'symbol',
  'span',
  'soon',
  'download',
  'knows_about',
  'area_served',
]);
/** Shared-value keys an editor rarely needs: collapsed under "Layout settings". */
export const LAYOUT_KEYS = new Set([
  'accent',
  'icon',
  'ratio',
  'key',
  'symbol',
  'span',
  'knows_about',
  'area_served',
]);

export function isSharedValueKey(key: string): boolean {
  return SHARED_VALUE_KEYS.has(key) || key.endsWith('_href');
}

const TAGS = /<[^>]+>/g;
const SUMMARY_KEYS = [
  'title',
  'heading',
  'label',
  'name',
  'eyebrow',
  'kicker',
  'lede',
  'body',
  'text',
];

/** Plain text of a copy string (copy may carry inline HTML such as `<em>`). */
export function plain(s: string): string {
  return s.replace(TAGS, '').replace(/\s+/g, ' ').trim();
}

/**
 * The first meaningful line of a region in one locale: its heading if it has one, else the
 * first prose string, searched breadth-first so `head.title` beats a deep list item.
 */
export function regionSummary(region: OutlineRegion, docs: Docs, locale: Locale): string {
  for (const ref of region.copy) {
    const root = refValue(docs, ref, locale);
    const found = firstText(root);
    if (found) return found.length > 140 ? `${found.slice(0, 139)}…` : found;
  }
  return '';
}

function firstText(root: Json | undefined): string {
  if (typeof root === 'string') return plain(root);
  let level: Json[] = root === undefined ? [] : [root];
  for (let depth = 0; depth < 6 && level.length; depth++) {
    for (const node of level) {
      if (!isPlainObject(node) || isImageNode(node)) continue;
      for (const key of SUMMARY_KEYS) {
        const v = node[key];
        if (typeof v === 'string' && plain(v)) return plain(v);
      }
    }
    const next: Json[] = [];
    for (const node of level) {
      if (Array.isArray(node)) next.push(...node);
      else if (isPlainObject(node) && !isImageNode(node))
        next.push(...Object.values(node as Record<string, Json>));
    }
    level = next;
  }
  return '';
}

export interface Coverage {
  /** Prose strings with English text. */
  total: number;
  /** …of those, how many also have Arabic. */
  translated: number;
}

/** Arabic coverage of a region's prose: every English string vs its Arabic twin. */
export function regionCoverage(region: OutlineRegion, docs: Docs): Coverage {
  const out: Coverage = { total: 0, translated: 0 };
  for (const ref of region.copy) {
    if (ref.bundled) continue;
    count(refValue(docs, ref, 'en'), refValue(docs, ref, 'ar'), '', out);
  }
  return out;
}

function count(en: Json | undefined, ar: Json | undefined, key: string, out: Coverage) {
  if (typeof en === 'string') {
    if (isSharedValueKey(key) || key === 'image_id' || !plain(en)) return;
    out.total++;
    if (typeof ar === 'string' && plain(ar)) out.translated++;
    return;
  }
  if (Array.isArray(en)) {
    en.forEach((v, i) => count(v, Array.isArray(ar) ? ar[i] : undefined, key, out));
    return;
  }
  if (isPlainObject(en)) {
    for (const [k, v] of Object.entries(en)) {
      if (k === 'image') continue; // hydrated read-side media, not copy
      count(v, isPlainObject(ar) ? ar[k] : undefined, k, out);
    }
  }
}

/** Whether a region's copy differs between two document sets (saved vs working). */
export function regionDirty(region: OutlineRegion, saved: Docs, working: Docs): boolean {
  return region.copy.some((ref) =>
    (['en', 'ar'] as const).some(
      (l) => JSON.stringify(refValue(saved, ref, l)) !== JSON.stringify(refValue(working, ref, l)),
    ),
  );
}

/** Other pages and screens that render the same subtree as `ref`. */
export function sharedWith(
  outlines: PageOutline[],
  selfKey: string,
  ref: OutlineCopyRef,
): PageOutline[] {
  return outlines.filter(
    (o) =>
      o.key !== selfKey &&
      o.regions.some((r) =>
        r.copy.some((c) => c.namespace === ref.namespace && c.path === ref.path),
      ),
  );
}

/** Where the editor for an outline lives, optionally opened on one region. */
export function editorPath(outline: PageOutline, regionId?: string | null): string {
  const base = outline.record
    ? `/pages/${outline.record.id}`
    : `/pages/screen/${encodeURIComponent(outline.key)}`;
  return regionId ? `${base}?region=${encodeURIComponent(regionId)}` : base;
}

/**
 * The page region that renders a page-copy field — how a search hit on `pg_products` →
 * `hero.title` lands on the Products page's top banner. CMS pages win over special
 * screens; among regions, the longest matching copy path wins.
 */
export function locateField(
  outlines: PageOutline[],
  namespace: string,
  fieldPath: string | null | undefined,
): { outline: PageOutline; region: OutlineRegion } | null {
  const path = (fieldPath ?? '').replace(/^(en|ar)\./, '');
  let best: { outline: PageOutline; region: OutlineRegion; score: number } | null = null;
  for (const outline of outlines) {
    for (const region of outline.regions) {
      for (const ref of region.copy) {
        if (ref.namespace !== namespace) continue;
        const match = !path || path === ref.path || path.startsWith(`${ref.path}.`);
        if (!match) continue;
        const score =
          (outline.record ? 1000 : 0) + (path ? ref.path.length : 0) - (ref.shared ? 1 : 0);
        if (!best || score > best.score) best = { outline, region, score };
      }
    }
  }
  return best && { outline: best.outline, region: best.region };
}

/** Console routes for the records a region lists. */
export function collectionRoutes(ref: OutlineCollectionRef): {
  list: string;
  item: (id: string) => string;
  create: string;
} {
  if (ref.collection === 'product-families') {
    return { list: '/families', item: (id) => `/families/${id}`, create: '/families/new' };
  }
  const base = `/content/${ref.collection}`;
  const query = new URLSearchParams(ref.filter ?? {}).toString();
  return {
    list: query ? `${base}?${query}` : base,
    item: (id) => `${base}/${id}`,
    create: `${base}/new`,
  };
}

/**
 * Stored copy keeps no meaningful key order (Postgres jsonb sorts keys), so fields are put
 * in the order a visitor reads them: label, heading, introduction, body, then lists and
 * buttons, then images and their notes. Unknown keys keep their relative order mid-way.
 */
const READING_ORDER = [
  'eyebrow',
  'kicker',
  'code',
  'title',
  'heading',
  'name',
  'label',
  'k',
  'v',
  'value',
  'unit',
  'lede',
  'summary',
  'intro',
  'head',
  'body',
  'text',
  'description',
  'note',
];
const TRAILING_ORDER = [
  'items',
  'rows',
  'checks',
  'figures',
  'table',
  'ctas',
  'cta',
  'link',
  'href',
  'more',
  'image',
  'credit',
  'caption',
  'alt',
  'foot',
];
export function readingOrder(keys: string[]): string[] {
  const rank = (k: string) => {
    const a = READING_ORDER.indexOf(k);
    if (a >= 0) return a;
    const b = TRAILING_ORDER.indexOf(k);
    return b >= 0 ? 1000 + b : 500;
  };
  return keys
    .map((k, i) => ({ k, i }))
    .sort((x, y) => rank(x.k) - rank(y.k) || x.i - y.i)
    .map((x) => x.k);
}
